import { Outlet } from 'react-router-dom'
import { AppSidebar } from './AppSidebar'
import { NotificationBell } from './NotificationBell'
import { useAuth } from '@/hooks/useAuth'
import { Loading } from '@/components/ui/loading'
import { Button } from '@/components/ui/button'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { UserDeactivatedModal } from '@/components/modals/UserDeactivatedModal'
import { ChatProvider } from '@/components/chat/ChatContext'
import { ChatWidget } from '@/components/chat/ChatWidget'
import { CompanySwitcher } from './CompanySwitcher'
import { ClinicBrandLogo } from './ClinicBrandLogo'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { getCompanyBrandStyle } from '@/lib/companyBranding'
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react'

export function Layout() {
  const {
    isLoading,
    isAuthenticated,
    login,
    logout,
    profile,
    profileSetupError,
    retryProfileSetup,
  } = useAuth()
  const companyBranding = useQuery(
    api.companies.getActiveCompanyBranding,
    isAuthenticated && profile?.active && profile.company_id ? {} : 'skip'
  )
  
  if (isLoading) {
    return <Loading text="Caricamento applicazione..." />
  }
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.svg" 
                alt="PrimoFastLab" 
                className="h-16 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">
              PrimoFastLab
            </h1>
            <p className="text-gray-600 mb-8">
              Sistema di gestione prescrizioni dentali
            </p>
          </div>
          
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Accedi al tuo account
              </h2>
              
              <Button 
                onClick={login}
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Accedi con Auth0
              </Button>
              
              <p className="mt-4 text-sm text-gray-500">
                Utilizza le tue credenziali per accedere al sistema
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (profileSetupError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <div className="w-full max-w-lg rounded-xl border border-red-200 bg-white p-6 shadow-sm sm:p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-gray-950">
            Accesso non completato
          </h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            Auth0 ha autenticato l’account, ma non è stato possibile collegarlo a un profilo FastLab.
          </p>
          <div role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-950">
            {profileSetupError}
          </div>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={logout} className="w-full sm:w-auto">
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              Esci
            </Button>
            <Button type="button" onClick={retryProfileSetup} className="w-full sm:w-auto">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Riprova
            </Button>
          </div>
        </div>
      </div>
    )
  }
  
  const isUserDeactivated = isAuthenticated && profile && !profile.active;
  
  return (
    <ChatProvider>
      <div
        className="flex min-h-screen flex-col bg-gray-50"
        style={getCompanyBrandStyle(
          companyBranding?.brandColor,
          companyBranding?.brandForeground
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <AppSidebar
            brandColor={companyBranding?.brandColor}
            clinicLogoUrl={companyBranding?.logoUrl}
            clinicName={companyBranding?.companyName}
          />
          <div className="flex min-w-0 min-h-0 flex-1 flex-col">
            <header
              className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white/95 px-4 backdrop-blur sm:px-6 lg:h-16"
              style={companyBranding?.brandColor
                ? { boxShadow: 'inset 0 -2px 0 var(--company-brand-marker)' }
                : undefined}
            >
              <div className="min-w-0 flex-1">
                <CompanySwitcher
                  brandColor={companyBranding?.brandColor}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                <NotificationBell />
                <ClinicBrandLogo
                  logoUrl={companyBranding?.logoUrl}
                  companyName={companyBranding?.companyName}
                  className="hidden h-9 border-l border-gray-200 pl-3 lg:flex"
                />
              </div>
            </header>
            <main
              className={`min-w-0 min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6 lg:py-6 ${isUserDeactivated ? 'pointer-events-none opacity-50' : ''}`}
            >
              <ErrorBoundary>
                <div className="mx-auto w-full max-w-7xl">
                  <Outlet />
                </div>
              </ErrorBoundary>
            </main>
          </div>
        </div>
        
        <UserDeactivatedModal isOpen={!!isUserDeactivated} />
        {!isUserDeactivated && <ChatWidget />}
      </div>
    </ChatProvider>
  )
}
